import type { CardComment, RedlineTextProposal } from "@common/utils/redline/wire/redline-payload";

import type { ActionToApply } from "../ApplyAllActionsPanel";
import type { CommentCardContent } from "../proposal-card/card-content";
import { cardCommentKeyOf, cardCommentSelections } from "../proposal-card/card-comment-selections";
import type { ProposalEditsByAction } from "../../../utils/proposal-edits-storage";
import type { TrackerCard } from "./tracker-types";

export interface CardCommentEntry {
  action: ActionToApply;
  card: TrackerCard;
}

function newParagraphOf(comment: CardComment, textRedline: RedlineTextProposal | null) {
  return textRedline?.insertions.find((insertion) => insertion.bookmark === comment.bookmark) ?? null;
}

function commentedParagraphTextOf(comment: CardComment, textRedline: RedlineTextProposal | null): string | null {
  return textRedline?.paragraphs.find((cardParagraph) => cardParagraph.bookmark === comment.bookmark)?.textProposed
    ?? newParagraphOf(comment, textRedline)?.textProposed
    ?? null;
}

function commentContentOf(comment: CardComment, textRedline: RedlineTextProposal | null): CommentCardContent {
  const newParagraph = newParagraphOf(comment, textRedline);
  if (newParagraph !== null) {
    return { kind: "comment", text: comment.text, bookmarks: [newParagraph.afterBookmark], anchorText: null, placement: "after_paragraph", paragraphText: newParagraph.textProposed, paragraphAnchor: null };
  }
  return { kind: "comment", text: comment.text, bookmarks: [comment.bookmark], anchorText: comment.anchor_text, placement: comment.placement, paragraphText: commentedParagraphTextOf(comment, textRedline), paragraphAnchor: null };
}

function entryOf(parentAction: ActionToApply, cardCommentKey: string, comment: CardComment, textRedline: RedlineTextProposal | null, edits: ProposalEditsByAction): CardCommentEntry {
  const isChecked = cardCommentSelections.isChecked(cardCommentKey);
  const edit = edits.get(cardCommentKey);
  const isApplied = parentAction.isApplied && isChecked;
  return {
    action: { id: cardCommentKey, cardNumber: parentAction.cardNumber, type: "comment", description: comment.text, isApplied, isSelected: isChecked, isDismissed: false },
    card: {
      actionId: cardCommentKey,
      cardNumber: parentAction.cardNumber,
      isApplied,
      supersededBy: null,
      textRedlineBeforeEdit: null,
      textRedlineWhenOpened: null,
      textBeforeEdit: edit?.kind === "comment" ? edit.textBeforeEdit : null,
      content: commentContentOf(comment, textRedline),
      changeThroughoutSelection: null,
      ridesWithCardId: parentAction.id,
    },
  };
}

export function cardCommentEntriesOf(actions: ActionToApply[], trackerCards: ReadonlyMap<string, TrackerCard>, edits: ProposalEditsByAction): CardCommentEntry[] {
  return actions.flatMap((parentAction) => {
    const parentCard = trackerCards.get(parentAction.id);
    if (parentCard === undefined || parentAction.isDismissed) return [];
    const content = parentCard.content;
    if (content.kind === "comment" || content.kind === "reply") return [];
    return content.comments.flatMap((comment) => {
      const cardCommentKey = cardCommentKeyOf(parentAction.id, comment);
      if (cardCommentKey === null) return [];
      return [entryOf(parentAction, cardCommentKey, comment, content.textRedline, edits)];
    });
  });
}
